import { ChangeDetectorRef, Component, Input, OnInit } from "@angular/core";
import { TableModule } from "primeng/table";
import { File } from "../types";
import { FilesManagerService } from "../files-manager.service";
import { MomentModule } from "ngx-moment";
import { Conversation } from "../../conversations/panels/conversation.model";
import { Subscription } from "rxjs";
import { WorkspacesService } from "../../main/accountant/workspaces.service";
import { FilePreviewService } from "../../main/common/file-preview.service";
import { MoveFileModalComponent } from "../../main/accountant/modals/move-file-modal.component";
import { BsModalService } from "ngx-bootstrap/modal";
import { HotToastService } from "@ngneat/hot-toast";
import { LocalStorageService } from "src/app/utils/localstorage.service";
import { Data as UserData } from "../../shared-data.service";

interface LazyLoadEvent {
  first: number;
  rows: number;
  sortField?: string;
  sortOrder?: number;
}
interface PagedRecentFilesResponse {
  files: File[];
  page: number;
  num_pages: number;
  count: number;
}

@Component({
    selector: "app-files-table-view",
    templateUrl: "./files-table-view.component.html",
    imports: [MomentModule,
        TableModule],
    styleUrls: ["./files-table-view.component.scss"]
})
export class FilesTableViewComponent implements OnInit {

  moment_calendar_format = {
    sameDay: "[Today]",
    nextDay: "[Tomorrow]",
    nextWeek: "dddd",
    lastDay: "[Yesterday]",
    lastWeek: "[Last] dddd",
    sameElse: "MMM D, YYYY",
  };

  isLoading = false;
  totalRecords = 0;
  sortOrder: number = -1;
  sortField: string = 'upload_date';
  rows: 10 | 25 | 50 | 100 | 1000 = 25;
  currPage = 0;
  numPages = 0;
  records: File[] = [];

  userData: UserData;
  conversation: Conversation;
  subscriptions = new Subscription();

  _listFiltering = 'from-client';
  _lastEvent: LazyLoadEvent = undefined;

  constructor(
    private cdr: ChangeDetectorRef,
    private filesManagerService: FilesManagerService,
    private filePreviewService: FilePreviewService,
    private localStorage: LocalStorageService,
    private modalService: BsModalService,
    private toastr: HotToastService,
    private workspacesService: WorkspacesService
  ) { }

  @Input() set listFiltering(value: string) {
    this._listFiltering = value;
    // do not run on first time
    if (this._lastEvent) this.load({ first: 0, ...this._lastEvent });
  }

  get listFiltering() {
    return this._listFiltering;
  }

  ngOnInit(): void {
    this.userData = this.localStorage.get("data");

    this.rows = this.localStorage.get("fileListRows") ?? this.rows;
    this.sortOrder = this.localStorage.get("fileListSortOrder") ?? this.sortOrder;
    this.sortField = this.localStorage.get("fileListSortField") ?? this.sortField;

    this.subscriptions.add(
      this.workspacesService.$conversation.subscribe((conversation) => {
        if (conversation != null) {
          this.conversation = conversation;
          this.reset();
          this.load({ first: 0, rows: this.rows });
        }
      }),
    );
  }

  reset() {
    this.totalRecords = 0;
    this.currPage = 0;
    this.numPages = 0;
    this.records = [];
  }

  ngAfterContentChecked() {
    this.cdr.detectChanges();
  }

  load(event: LazyLoadEvent) {
    if (!this.conversation) return;
    this.updatePreferences(event);

    this.isLoading = true;
    this._lastEvent = event;
    let page = Math.ceil((event.first + 1) / event.rows);
    this.filesManagerService.getPagedFiles(
      this.listFiltering,
      this.conversation.id,
      this.rows,
      page,
      this.sortField,
      this.sortField != undefined ? this.sortOrder > 0 ? "asc" : "desc" : undefined
    ).subscribe({
      next: (res: PagedRecentFilesResponse) => {
        this.records = res.files;
        this.totalRecords = res.count;
        this.currPage = res.page;
        this.numPages = res.num_pages;
      },
      complete: () => this.isLoading = false
    });
  }

  private updatePreferences(event: LazyLoadEvent) {
    if (event.sortOrder) {
      this.localStorage.set("fileListSortOrder", event.sortOrder);
      this.sortOrder = event.sortOrder;
    }
    if (event.sortField) {
      this.localStorage.set("fileListSortField", event.sortField);
      this.sortField = event.sortField;
    }
    this.localStorage.set("fileListRows", event.rows);
    this.rows = event.rows as 10 | 25 | 50 | 100 | 1000;
  }

  preview(etag: string) {
    this.isLoading = true;
    this.filesManagerService
      .getFileDetails(this.conversation.id, etag)
      .then((res: any) => {
        this.isLoading = false;
        this.filePreviewService.openFile.next({ file: res.file });
      });
  }

  move(e: Event, file: File) {
    e.preventDefault();
    e.stopPropagation();
    const initialState = {
      currentConversationID: this.conversation.id,
      fileDetails: {
        filename: file.filename,
        etag: file.etag,
      },
    };

    const moveFileModal = this.modalService.show(MoveFileModalComponent, {
      initialState,
    });
    moveFileModal.content.onClose.subscribe(
      async ({ target_uuid, _ }) => {
        if (!target_uuid) {
          moveFileModal.hide();
        }
        const payload: any = {
          folder: target_uuid,
          file: file.etag,
        };

        this.filesManagerService
          .moveFile(this.conversation.id, payload)
          .then(
            _ => {
              moveFileModal.hide();
              this.load(this._lastEvent);
              this.toastr.success(`File successfully moved.`);
            },
            (error) => {
              console.log("move failed", error);
              moveFileModal.hide();
              this.toastr.error("Something went wrong when moving the file.");
            },
          );
      });
  }
}